import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { SupplierStatementView } from '@wodi/shared';
import {
  Cell,
  Column,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { supplierStatementQuery } from '../api/procurement.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatIsoDate } from '../patients/format.ts';
import { LoadFailed } from '../procurement/common.tsx';
import { documentTag, monthOf, orderStateWords, todayEat } from '../procurement/format.ts';
import { formatCents } from '../stock/format.ts';
import styles from '../stock/stock.module.css';

// A supplier statement (docs/plans/phase2-procurement.md §5.11). TODO(business): supplier
// payments are not recorded in Wodi, so the balance is approved invoices only (plan §12, 6).

const route = getRouteApi('/_app/procurement/suppliers/$supplierId/statement');

export function SupplierStatementPage() {
  const { supplierId } = route.useParams();
  const month = monthOf(todayEat());
  const [from, setFrom] = useState<string | null>(month.from);
  const [to, setTo] = useState<string | null>(month.to);
  const ready = from !== null && to !== null && from <= to;
  const statement = useQuery({
    ...supplierStatementQuery(supplierId, from ?? '', to ?? ''),
    enabled: ready,
  });
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement/suppliers">Back to supplier statements</Link>
      </p>
      <PageHeader title={`Statement: ${statement.data?.supplier.name ?? 'supplier'}`} />
      <div className={styles.toolbar}>
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField
          label="To"
          value={to}
          onChange={setTo}
          {...(from !== null && to !== null && from > to
            ? { errorMessage: 'The end is before the start.' }
            : {})}
        />
      </div>
      <Notice tone="info">
        Supplier payments are not recorded in Wodi. The balance is the approved invoices; check
        payments in your accounts.
      </Notice>
      {ready && statement.isPending ? (
        <LoadingBlock label="Loading the statement" rows={4} />
      ) : null}
      {statement.isError ? (
        <LoadFailed
          error={statement.error}
          fallback="The statement couldn't be loaded."
          onRetry={() => void statement.refetch()}
        />
      ) : null}
      {statement.isSuccess ? <Statement statement={statement.data} /> : null}
    </div>
  );
}

function Statement({ statement }: { statement: SupplierStatementView }) {
  return (
    <>
      <Section title={`${formatIsoDate(statement.from)} to ${formatIsoDate(statement.to)}`}>
        <p className={styles.numbers}>
          Owed before the period: {formatCents(statement.opening.cents)}
        </p>
        <TableScroll>
          <Table flat aria-label="Approved invoices in the period">
            <TableHeader>
              <Column isRowHeader>Date</Column>
              <Column>Invoice</Column>
              <Column>Their number</Column>
              <Column>LPO</Column>
              <Column numeric>Amount</Column>
              <Column numeric>Balance</Column>
            </TableHeader>
            <TableBody
              items={statement.rows.map((r) => ({ ...r, id: r.invoiceId }))}
              renderEmptyState={() => 'No approved invoices in this period.'}
            >
              {(r) => (
                <Row id={r.invoiceId}>
                  <Cell>{formatIsoDate(r.invoiceDate)}</Cell>
                  <Cell>
                    <Link to="/procurement/invoices/$invoiceId" params={{ invoiceId: r.invoiceId }}>
                      <span className="wodi-id">{r.number}</span>
                    </Link>
                  </Cell>
                  <Cell>
                    <span className="wodi-id">{r.supplierReference}</span>
                  </Cell>
                  <Cell>
                    <span className="wodi-id">{r.orderNumber}</span>
                  </Cell>
                  <Cell numeric>{formatCents(r.amount.cents)}</Cell>
                  <Cell numeric>{formatCents(r.balance.cents)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
        <p className={styles.total}>Owed at the end: {formatCents(statement.closing.cents)}</p>
      </Section>
      <Section title="Invoices waiting for approval">
        {statement.awaitingApproval.length === 0 ? (
          <p className={styles.meta}>None.</p>
        ) : (
          <ul>
            {statement.awaitingApproval.map((i) => (
              <li key={i.invoiceId}>
                <Link to="/procurement/invoices/$invoiceId" params={{ invoiceId: i.invoiceId }}>
                  <span className="wodi-id">{i.number}</span>
                </Link>{' '}
                ({formatIsoDate(i.invoiceDate)}), {formatCents(i.amount.cents)}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Received but not yet invoiced">
        {statement.receivedNotInvoiced.length === 0 ? (
          <p className={styles.meta}>Everything received has been invoiced.</p>
        ) : (
          <ul>
            {statement.receivedNotInvoiced.map((r) => (
              <li key={r.orderId} className={styles.numbers}>
                <Link to="/procurement/orders/$orderId" params={{ orderId: r.orderId }}>
                  <span className="wodi-id">{r.orderNumber}</span>
                </Link>
                : received {formatCents(r.received.cents)}, invoiced {formatCents(r.invoiced.cents)}
                , difference {formatCents(r.difference.cents)}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Open LPOs">
        {statement.openOrders.length === 0 ? (
          <p className={styles.meta}>No open LPOs.</p>
        ) : (
          <ul>
            {statement.openOrders.map((o) => (
              <li key={o.orderId}>
                <Link to="/procurement/orders/$orderId" params={{ orderId: o.orderId }}>
                  <span className="wodi-id">{o.number}</span>
                </Link>{' '}
                {formatCents(o.total.cents)}{' '}
                <Tag state={documentTag(o.status)}>
                  {orderStateWords(o.status, o.receiptStatus)}
                </Tag>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
